import { useState } from 'react'
import { fbt } from 'fbtee'
import { linkBrandLabel } from '../../lib/utils'
import { provisionalMapLabel } from '../../lib/commonStrings'
import { MapPin, Calendar, ExternalLink, Globe, Ticket, Maximize2, DollarSign, User, ShieldCheck } from 'lucide-react'
import { ZoomableImage } from '../../components/ZoomableImage'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../components/ui/dialog'
import { ReportEventButton } from './ReportEventButton'
import { track } from '../../lib/analytics'
import type { Event, Venue } from '../../types'

const CURRENCY_SYMBOL = /[$€£¥₹₽¢₩₪₫₱₺]/

function hasCurrencySymbol(price: string): boolean {
  return CURRENCY_SYMBOL.test(price)
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  )
}

function SpotifyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.5 17.32a.75.75 0 0 1-1.03.25c-2.82-1.72-6.37-2.11-10.55-1.16a.75.75 0 1 1-.34-1.46c4.57-1.04 8.5-.6 11.66 1.34.36.21.47.68.26 1.03zm1.47-3.27a.94.94 0 0 1-1.29.31c-3.23-1.99-8.16-2.56-11.98-1.4a.94.94 0 1 1-.55-1.8c4.37-1.33 9.81-.69 13.51 1.6.45.27.59.86.31 1.29zm.13-3.4c-3.87-2.3-10.27-2.51-13.97-1.39a1.13 1.13 0 1 1-.66-2.16c4.25-1.29 11.31-1.04 15.78 1.62a1.13 1.13 0 0 1-1.15 1.94z" />
    </svg>
  )
}

interface OfficialLinkProps {
  href: string
  icon: React.ReactNode
  label: React.ReactNode
  onClick: () => void
}

function OfficialLink({ href, icon, label, onClick }: OfficialLinkProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={onClick}
      className="flex items-center gap-1.5 text-sm text-muted-foreground"
    >
      {icon}
      <span className="text-primary hover:underline">{label}</span>
      <ExternalLink className="w-3 h-3 shrink-0" />
    </a>
  )
}

export function InfoView({
  event,
  dateRange,
  venue,
  // A party stacks everything on one page, so its report link belongs at the
  // very bottom of that page rather than in the middle of the info block.
  showReport = true,
}: {
  event: Event
  dateRange: string
  venue?: Venue | null
  showReport?: boolean
}) {
  const [mapOpen, setMapOpen] = useState(false)
  const tickets = event.urls?.tickets
  const ticketsBrand = tickets ? linkBrandLabel(tickets) : null
  const website = event.urls?.website
  const instagram = event.urls?.instagram
  const spotifyPlaylist = event.urls?.spotifyPlaylist
  const hasAnyLink = Boolean(tickets || website || instagram || spotifyPlaylist)

  function trackLink(linkType: 'website' | 'instagram' | 'spotify_playlist' | 'tickets') {
    track('select_content', {
      content_type: 'event_official_link',
      kind: linkType,
      event_id: event.id,
    })
  }

  return (
    <div className="space-y-4">
      {event.mapImageUrl && event.type === 'festival' && (
        <>
          <div className="relative w-full h-72 rounded-lg overflow-hidden bg-muted border border-border touch-none">
            <ZoomableImage
              src={event.mapImageUrl}
              alt={fbt('Festival map', 'Alt text for the festival venue map image').toString()}
              maxScale={6}
              wheel={false}
              lazy
            />
            <button
              type="button"
              onClick={() => setMapOpen(true)}
              aria-label={fbt('Expand festival map', 'Accessible label for the button that opens the festival map in a fullscreen dialog').toString()}
              className="absolute top-2 right-2 z-10 cursor-pointer flex items-center justify-center w-8 h-8 rounded-md bg-background/80 backdrop-blur-sm border border-border text-foreground hover:bg-background transition-colors"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-muted-foreground text-center -mt-2">
            <fbt desc="Hint shown under the festival map image telling users they can pinch to zoom the image inline">
              Pinch to zoom
            </fbt>
          </p>
          {event.mapImageFromEventId && (
            <p className="text-xs text-muted-foreground text-center -mt-3">{provisionalMapLabel()}</p>
          )}

          <Dialog open={mapOpen} onOpenChange={setMapOpen}>
            <DialogContent className="max-w-[95vw] sm:max-w-[95vw] w-full p-2 gap-2">
              <DialogTitle className="sr-only">
                <fbt desc="Accessible title for the fullscreen festival map dialog">Festival map</fbt>
              </DialogTitle>
              <DialogDescription className="sr-only">
                <fbt desc="Accessible description of the fullscreen festival map dialog, hidden visually but read by screen readers">
                  Pinch or scroll to zoom the festival venue map.
                </fbt>
              </DialogDescription>
              <div className="relative w-full h-[80vh] rounded bg-muted overflow-hidden">
                <ZoomableImage
                  src={event.mapImageUrl}
                  alt={fbt('Festival map', 'Alt text for the fullscreen festival map image').toString()}
                />
              </div>
              <a
                href={event.mapImageUrl}
                target="_blank"
                rel="noreferrer"
                className="block text-xs text-primary hover:underline text-center pb-1"
              >
                <fbt desc="Link inside the map dialog that opens the raw image in a new browser tab for easier zoom on desktop">
                  Open in new tab
                </fbt>
              </a>
            </DialogContent>
          </Dialog>
        </>
      )}

      {(() => {
        const locationText = venue
          ? `${venue.name}${venue.city ? `, ${venue.city}` : ''}${venue.state ? `, ${venue.state}` : ''}`
          : event.venueName ?? event.location
        const mapsQuery = venue
          ? venue.address ?? [venue.name, venue.city, venue.state].filter(Boolean).join(', ')
          : event.venueName ?? event.location
        const mapsUrl = mapsQuery
          ? `https://maps.google.com/?q=${encodeURIComponent(mapsQuery)}`
          : null
        return mapsUrl ? (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noreferrer"
            onClick={() => track('select_content', {
              content_type: 'event_location',
              event_id: event.id,
            })}
            className="flex items-center gap-1.5 text-sm text-muted-foreground"
          >
            <MapPin className="w-3.5 h-3.5 shrink-0" />
            <span className="text-primary hover:underline">{locationText}</span>
            <ExternalLink className="w-3 h-3 shrink-0" />
          </a>
        ) : (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <MapPin className="w-3.5 h-3.5 shrink-0" />
            <span>{locationText}</span>
          </div>
        )
      })()}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <Calendar className="w-3.5 h-3.5 shrink-0" />
          {dateRange}
        </span>
        {event.price && (
          <span className="flex items-center gap-1.5">
            {/* Most seeded prices already read "$25 – $40"; a coin icon in front
                of that just looks like a doubled currency symbol. */}
            {!hasCurrencySymbol(event.price) && <DollarSign className="w-3.5 h-3.5 shrink-0" />}
            {event.price}
          </span>
        )}
      </div>
      {event.ageRestriction && (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
          <span>{event.ageRestriction}</span>
        </div>
      )}
      {event.organizer && (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <User className="w-3.5 h-3.5 shrink-0" />
          <span>{event.organizer}</span>
        </div>
      )}

      {event.description && (
        <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
          {event.description}
        </p>
      )}

      {hasAnyLink && (
        <div className="flex flex-col gap-2 pt-1">
          {website && (
            <OfficialLink
              href={website}
              icon={<Globe className="w-3.5 h-3.5 shrink-0" />}
              label={<fbt desc="Link label for the festival's official website">Website</fbt>}
              onClick={() => trackLink('website')}
            />
          )}
          {instagram && (
            <OfficialLink
              href={instagram}
              icon={<InstagramIcon className="w-3.5 h-3.5 shrink-0" />}
              label={<fbt desc="Link label for the festival's Instagram profile">Instagram</fbt>}
              onClick={() => trackLink('instagram')}
            />
          )}
          {spotifyPlaylist && (
            <OfficialLink
              href={spotifyPlaylist}
              icon={<SpotifyIcon className="w-3.5 h-3.5 shrink-0" />}
              label={
                <fbt desc="Link label for the festival's official Spotify lineup playlist">
                  Spotify playlist
                </fbt>
              }
              onClick={() => trackLink('spotify_playlist')}
            />
          )}
          {tickets && (
            <OfficialLink
              href={tickets}
              icon={<Ticket className="w-3.5 h-3.5 shrink-0" />}
              // Naming the seller tells you whether you already have an account
              // there before you leave the app.
              label={
                ticketsBrand ? (
                  <fbt desc="Link label for the ticket page, naming the ticketing site">
                    Tickets:{' '}
                    <fbt:param name="site">{ticketsBrand}</fbt:param>
                  </fbt>
                ) : (
                  <fbt desc="Link label for the festival's ticket purchase page">Tickets</fbt>
                )
              }
              onClick={() => trackLink('tickets')}
            />
          )}
        </div>
      )}

      {showReport && <ReportEventButton eventId={event.id} />}
    </div>
  )
}
